import type { ActorKind } from "../../audit/actor.ts";
import type { Approval } from "../../invoicing/approvals.ts";
import { ARCHIVE_YEARS } from "../../invoicing/constants.ts";
import { deDate, eurUi, hoursDe, lineTotalCents, percentOfCents, toCents } from "../../invoicing/money.ts";
import { PAGE_WIDTH, TABLE } from "../../invoicing/pdf/layout.ts";
import type { PositionBox } from "../../invoicing/pdf/render.ts";
import { DOC_TYPE_LABELS, type DocStatus, type Document, type Position, type PositionSource } from "../../invoicing/types.ts";
import { clock, localDate, today } from "./format";
import type { Finding, Validation } from "../../invoicing/validate.ts";

export { hoursDe };
export { documentLabel } from "../../invoicing/types.ts";

export type Stage = "draft" | "open" | "paid" | "ready";
export type DocArt = "rechnungen" | "angebote" | "gutschriften";

const STAGES: readonly string[] = ["draft", "open", "paid", "ready"];
export const ARTS: { key: DocArt; label: string }[] = [
  { key: "rechnungen", label: "Rechnungen" },
  { key: "angebote", label: "Angebote" },
  { key: "gutschriften", label: "Gutschriften" },
];

/** `?stufe=`: an unknown value means no stage. */
export function stageFilter(stage: string | null): Stage | null {
  return stage !== null && STAGES.includes(stage) ? (stage as Stage) : null;
}

export function artFilter(art: string | null): DocArt {
  return ARTS.some((a) => a.key === art) ? (art as DocArt) : "rechnungen";
}

export function listFooter(shown: number, total: number, filtered: boolean): string {
  return filtered
    ? `${shown} von ${total} Rechnungen`
    : `${total} Rechnungen · Entwürfe bekommen ihre Nummer erst beim Versand, so bleibt der Nummernkreis lückenlos.`;
}

export function statusTone(status: DocStatus): "open" | "done" | "neutral" {
  switch (status) {
    case "open":
    case "partially_paid":
    case "sent":
    case "accepted":
    case "partially_invoiced":
      return "open";
    case "paid":
    case "invoiced":
    case "cancelled":
      return "done";
    default:
      return "neutral";
  }
}

/** Cents → "1.234,56 €". */
export function centsEur(cents: number): string {
  return `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)} €`;
}

/** Where the highlighter sits on a page image `pageWidthPx` wide (boxes are pt from the page's top edge, A4 is 595.28 pt wide). */
export function highlightRect(box: PositionBox, pageWidthPx: number): { top: number; height: number } {
  const scale = pageWidthPx / PAGE_WIDTH;
  return { top: box.top * scale, height: (box.bottom - box.top) * scale };
}

/** The page a position starts on (a split row has a box per page), null when it is not on the preview. */
export function pageOfPosition(boxes: PositionBox[], positionId: number): number | null {
  return boxes.find((b) => b.positionId === positionId)?.page ?? null;
}

/** „115 €“, „112,50 €“: whole euros without decimals. */
function rate(cents: number): string {
  return cents % 100 === 0 ? `${eurUi(cents).slice(0, -5)} €` : eurUi(cents);
}

/** The chip on a position: „5 Std × 115 €“, „Pauschale“, „Ausgabe · Fly.io“, „Angebot AN-1005“, „Abzug“; manual positions have none. */
export function sourceLabel(source: PositionSource | null, offerNumber: string | null = null): string | null {
  if (!source) return null;
  switch (source.kind) {
    case "time":
      return `${hoursDe(source.hours * 60)} Std × ${rate(source.rateCents)}`;
    case "flat":
      return "Pauschale";
    case "expense":
      return `Ausgabe · ${source.payee}`;
    case "offer":
      return offerNumber ? `Angebot ${offerNumber}` : "Angebot";
    case "deduction":
      return "Abzug";
  }
}

const BY: Record<ActorKind, string> = { user: "von dir", agent: "vom Agent", system: "vom Zeitplan" };
const pad2 = (n: number) => String(n).padStart(2, "0");

/** The line under the editor's title: the number the draft will get, and who saved last. */
export function savedLine(nextNumber: string | null, last: { name: string; kind: ActorKind; at: string } | null, now: Date = new Date()): string {
  const number = nextNumber ? `Wird beim Versand zu ${nextNumber}` : "Bekommt die Nummer beim Versand";
  if (!last) return `${number} · automatisch gespeichert`;
  const at = new Date(last.at);
  const sameDay = at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  const day = sameDay ? "" : ` am ${pad2(at.getDate())}.${pad2(at.getMonth() + 1)}.`;
  return `${number} · automatisch gespeichert, zuletzt ${BY[last.kind]}${day} um ${pad2(at.getHours())}:${pad2(at.getMinutes())}`;
}

/** „angefragt um 10:42“, from another day „angefragt am 03.11. um 10:42“. */
export function askedAt(iso: string, now: Date = new Date()): string {
  const day = localDate(iso);
  const date = day === today(now) ? "" : ` am ${day.slice(8, 10)}.${day.slice(5, 7)}.`;
  return `angefragt${date} um ${clock(iso)}`;
}

/** The sub line of an approval in „Braucht dich“: „2.144,30 € · 7 von 7 Prüfungen bestanden · angefragt um 10:49“. */
export function needsYouSub(approval: Approval, totalCents: number, validation: Pick<Validation, "passed" | "checks">, now: Date = new Date()): string {
  return `${centsEur(totalCents)} · ${checkSummaryLine({ passed: validation.passed, total: validation.checks.length })} · ${askedAt(approval.requestedAt, now)}`;
}

export interface CheckSummary { passed: number; total: number; extra: Finding[]; allPassed: boolean }

/**
 * The check list's count. Errors outside the eight rules (e.g. stale deductions, no recipient e-mail) never show in
 * `checks`; each counts as one more failed check, so the list never reads as all passed while sending is blocked.
 */
export function checkSummary(v: Validation): CheckSummary {
  const extra = v.errors.filter((e) => e.rule === null);
  const total = v.checks.length + extra.length;
  return { passed: v.passed, total, extra, allPassed: v.ok && v.passed === total };
}

export function checkSummaryLine(s: Pick<CheckSummary, "passed" | "total">): string {
  return `${s.passed} von ${s.total} Prüfungen bestanden`;
}

/** The highlighter spans the positions table plus a little air, in px on a page image `pageWidthPx` wide. */
export function highlightSpan(pageWidthPx: number): { left: number; width: number } {
  const scale = pageWidthPx / PAGE_WIDTH;
  return { left: (TABLE.x - 4) * scale, width: (TABLE.width + 8) * scale };
}

/** `ids` with `id` one step up (-1) or down (+1); null when it cannot move. */
export function movePosition(ids: number[], id: number, delta: -1 | 1): number[] | null {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/** For the live region after a keyboard or menu move; `n` is the position's number before the move. */
export function moveAnnouncement(n: number, delta: -1 | 1): string {
  return `Position ${n} nach ${delta < 0 ? "oben" : "unten"} verschoben`;
}

/** date + days, both calendar days („YYYY-MM-DD“). */
function addDays(day: string, days: number): string {
  const d = new Date(Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10) + days));
  return d.toISOString().slice(0, 10);
}

/** The send dialog's line under the title: „2.144,30 € an …, fällig am 17.11.2026. Ab dem Senden ist die Rechnung festgeschrieben.“ */
export function sendSummary(doc: Document, number: string | null, mode: "send" | "finalize" = "send"): string {
  const offer = doc.type === "offer";
  const due = offer ? doc.validUntil : doc.date && doc.timeToPay !== null ? addDays(doc.date, doc.timeToPay) : doc.dueDate;
  const when = due ? `, ${offer ? "gültig bis" : "fällig am"} ${deDate(due)}` : "";
  const fixed = doc.finalizedAt !== null
    ? `${number ?? doc.number} ist schon festgeschrieben.`
    : mode === "finalize"
      ? `Festschreiben vergibt die Nummer${number ? ` ${number}` : ""}, eine E-Mail geht nicht raus.`
      : `Ab dem Senden ist ${offer ? "das Angebot" : "die Rechnung"} festgeschrieben.`;
  return `${eurUi(doc.totalCents)} an ${doc.clientName}${when}. ${fixed}`;
}

/** Over the stored PDF on the detail page (BAO §132: kept to the end of the document's year + 7). */
export function archiveLine(date: string): string {
  return `Festgeschrieben. Inhalt und PDF bleiben so, bis 31.12.${Number(date.slice(0, 4)) + ARCHIVE_YEARS} archiviert.`;
}

/** Errors the send dialog itself settles: a recipient typed into „An“, the Kleinunternehmer override checkbox. */
const SETTLED_IN_DIALOG = ["recipient_email_missing", "ku_threshold_exceeded"];

/** The errors that block finalizing a draft from the send dialog; the dialog settles the rest itself. */
export function dialogBlockers(v: Validation): Finding[] {
  return v.errors.filter((e) => !SETTLED_IN_DIALOG.includes(e.code));
}

/** Why „Prüfen & senden“ cannot open the dialog; null when the dialog can deal with what is left. */
export function sendBlocker(v: Validation): string | null {
  return dialogBlockers(v)[0]?.message ?? null;
}

/** A send claimed `sent_at` but never got to `sent_to`: the process died while mailing. */
export function sendStuck(doc: Pick<Document, "finalizedAt" | "sentAt" | "sentTo">): boolean {
  return doc.finalizedAt !== null && doc.sentAt !== null && doc.sentTo === null;
}

export interface ApprovalNotice { tone: "gone" | "failed" | "unknown" | "done"; text: string }

const GONE: Partial<Record<Approval["status"], (a: Approval) => string>> = {
  void: () => "Das Dokument wurde seit der Anfrage geändert",
  expired: () => "Sie ist nach 7 Tagen abgelaufen",
  rejected: (a) => (a.reason ? `Du hast sie abgelehnt (${a.reason})` : "Du hast sie abgelehnt"),
};

/** What a request says once it is no longer open; null while it is. */
export function approvalNotice(a: Approval): ApprovalNotice | null {
  if (a.status === "pending") return null;
  const gone = GONE[a.status];
  if (gone) return { tone: "gone", text: `Diese Anfrage gilt nicht mehr: ${gone(a)}. Der Agent muss neu anfragen.` };
  if (a.status === "failed") return { tone: "failed", text: `Fehlgeschlagen: ${a.error ?? "unbekannter Fehler"}` };
  // Claimed, but neither a result nor an error was stored: the process stopped in between. Florian's own override
  // (requested and decided by him in one step) never has a result.
  if (a.result === null && a.error === null && a.requestedBy.kind !== "user") return { tone: "unknown", text: "Ergebnis unbekannt – im Verlauf prüfen" };
  return { tone: "done", text: "Freigegeben" };
}

const MAIL_FIELDS = [["to", "An"], ["subject", "Betreff"], ["text", "Nachricht"]] as const;

/** The mail fields of a request that differ from the standard texts (the approval's title names only the customer). */
export function mailChanges(args: Record<string, unknown>, defaults: { to: string | null; subject: string; text: string }): string[] {
  return MAIL_FIELDS.filter(([k]) => typeof args[k] === "string" && args[k] !== (defaults[k] ?? "")).map(([, label]) => label);
}

export type ConvertChoice =
  | { kind: "full" }
  | { kind: "percent"; percent: number }
  | { kind: "amount"; amount: number }
  | { kind: "positions"; lines: { positionId: number; share: number }[] }
  | { kind: "final" };

/** What the invoice drafted from an offer comes to, computed as `convertOffer` does. */
export function convertAmountCents(offer: { totalCents: number; positions: Position[] }, billedCents: number, mode: ConvertChoice): number {
  switch (mode.kind) {
    case "full":
      return offer.totalCents;
    case "percent":
      return percentOfCents(offer.totalCents, mode.percent);
    case "amount":
      return toCents(mode.amount);
    case "positions":
      return mode.lines.reduce((sum, l) => {
        const p = offer.positions.find((x) => x.id === l.positionId);
        if (!p) return sum;
        if (l.share === 1) return sum + p.totalCents;
        return sum + lineTotalCents(Math.round(p.quantity * l.share * 10_000) / 10_000, p.unitPriceCents);
      }, 0);
    case "final":
      return offer.totalCents - billedCents;
  }
}

/** „Alle Änderungen an RE-1086 im Verlauf →“; without a number all document changes. */
export function historyHref(number: string | null): string {
  const sp = new URLSearchParams({ bereich: "document" });
  if (number) sp.set("q", number);
  return `/verlauf?${sp}`;
}

/** „Rechnung_RE-1086.pdf“, as the server names the file. */
export function pdfName(doc: Pick<Document, "type">, number: string | null): string {
  return `${DOC_TYPE_LABELS[doc.type]}_${number ?? "Entwurf"}.pdf`;
}

/** The detail page's „Fehler entdeckt?“ text, also the cancel dialog's. */
export function cancelText(doc: Pick<Document, "number" | "totalCents">): string {
  return `Stornieren erstellt eine Stornorechnung über −${centsEur(doc.totalCents)}. ${doc.number} bleibt im Archiv, danach schreibst du die korrigierte Rechnung.`;
}
